































import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../styles/AdminSchedule.css";

const AdminSchedule = () => {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [ports, setPorts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [activeTab, setActiveTab] = useState("routes"); // routes, vessels, ports, schedules

  // Form visibility states
  const [showRouteForm, setShowRouteForm] = useState(false);
  const [showVesselForm, setShowVesselForm] = useState(false);
  const [showPortForm, setShowPortForm] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);

  // Form data states
  const [routeForm, setRouteForm] = useState({ direction: "" });
  const [vesselForm, setVesselForm] = useState({
    name: "",
    route_id: "",
    type: "",
    capacity: ""
  });
  const [portForm, setPortForm] = useState({
    name: "",
    country: "",
    code: ""
  });
  const [scheduleForm, setScheduleForm] = useState({
    vessel_id: "",
    port_id: "",
    dates: "",
    eta: "",
    etd: "",
    route_key: "" // Track the selected route and reset vessel when route changes
  });

  // Edit states
  const [editingId, setEditingId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Search states
  const [routeSearch, setRouteSearch] = useState("");
  const [vesselSearch, setVesselSearch] = useState("");
  const [portSearch, setPortSearch] = useState("");
  const [scheduleSearch, setScheduleSearch] = useState("");

  // Pagination states
  const [currentRoutePage, setCurrentRoutePage] = useState(1);
  const [currentVesselPage, setCurrentVesselPage] = useState(1);
  const [currentPortPage, setCurrentPortPage] = useState(1);
  const [currentSchedulePage, setCurrentSchedulePage] = useState(1);
  const itemsPerPage = 5;

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [routesRes, vesselsRes, portsRes, schedulesRes] = await Promise.all([
        axios.get('https://ecl.net.in/backende/api/sailing-routes'),
        axios.get('https://ecl.net.in/backende/api/vessels'),
        axios.get('https://ecl.net.in/backende/api/ports'),
        axios.get('https://ecl.net.in/backende/api/schedules')
      ]);

      // Set fresh data to avoid any state dependencies
      setRoutes(routesRes.data);
      setVessels(vesselsRes.data);
      setPorts(portsRes.data);
      setSchedules(schedulesRes.data);

      // Initialize form states if needed
      if (routesRes.data.length > 0 && !vesselForm.route_id) {
        setVesselForm(prev => ({
          ...prev,
          route_id: routesRes.data[0].id
        }));
      }

      setLoading(false);
    } catch (err) {
      setError("Failed to load data. Please try again.");
      setLoading(false);
      console.error("Error fetching data:", err);
    }
  }, [vesselForm.route_id]);

  useEffect(() => {
    // Check if user is admin
    const isAdmin = sessionStorage.getItem('isAdmin');
    if (isAdmin !== 'true') {
      navigate('/login');
      return;
    }

    fetchAllData();
  }, [navigate, fetchAllData]);

  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(""), 3000);
  };

  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(""), 3000);
  };

  // Route CRUD operations
  const handleAddRoute = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('direction', routeForm.direction);

      const response = await axios.post('https://ecl.net.in/backende/api/sailing-routes', formData);

      if (response.data.success) {
        setRouteForm({ direction: "" });
        setShowRouteForm(false);
        fetchAllData();
        showSuccess("Route added successfully!");
      } else {
        showError(response.data.message || "Failed to add route");
      }
    } catch (err) {
      showError("Failed to add route");
      console.error(err);
    }
  };

  const handleEditRoute = (route) => {
    // Create a fresh copy without reference to the original object
    setRouteForm({
      direction: route.direction
    });
    setEditingId(route.id);
    setIsEditing(true);
    setShowRouteForm(true);
  };

  const handleUpdateRoute = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('direction', routeForm.direction);

      const response = await axios.put(`https://ecl.net.in/backende/api/sailing-routes/${editingId}`, formData);

      if (response.data.success) {
        // Reset form state completely
        setRouteForm({ direction: "" });
        setEditingId(null);
        setIsEditing(false);
        setShowRouteForm(false);
        // Fetch fresh data to ensure proper state updates
        fetchAllData();
        showSuccess("Route updated successfully!");
      } else {
        showError(response.data.message || "Failed to update route");
      }
    } catch (err) {
      showError("Failed to update route");
      console.error(err);
    }
  };

  const handleDeleteRoute = async (routeId) => {
    if (window.confirm("Are you sure? This will delete all vessels and schedules for this route.")) {
      try {
        const response = await axios.delete(`https://ecl.net.in/backende/api/sailing-routes/${routeId}`);

        if (response.data.success) {
          fetchAllData();
          showSuccess("Route deleted successfully!");
        } else {
          showError(response.data.message || "Failed to delete route");
        }
      } catch (err) {
        showError("Failed to delete route");
        console.error(err);
      }
    }
  };

  // Vessel CRUD operations
  const handleAddVessel = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('name', vesselForm.name);
      formData.append('route_id', vesselForm.route_id);
      if (vesselForm.type) formData.append('type', vesselForm.type);
      if (vesselForm.capacity) formData.append('capacity', vesselForm.capacity);

      const response = await axios.post('https://ecl.net.in/backende/api/vessels', formData);

      if (response.data.success) {
        setVesselForm({ 
          ...vesselForm, 
          name: "", 
          type: "", 
          capacity: "" 
        });
        setShowVesselForm(false);
        fetchAllData();
        showSuccess("Vessel added successfully!");
      } else {
        showError(response.data.message || "Failed to add vessel");
      }
    } catch (err) {
      showError("Failed to add vessel");
      console.error(err);
    }
  };

  const handleEditVessel = (vessel) => {
    setVesselForm({
      name: vessel.name,
      route_id: vessel.route_id,
      type: vessel.type || "",
      capacity: vessel.capacity || ""
    });
    setEditingId(vessel.id);
    setIsEditing(true);
    setShowVesselForm(true);
  };

  const handleUpdateVessel = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('name', vesselForm.name);
      formData.append('route_id', vesselForm.route_id);
      if (vesselForm.type) formData.append('type', vesselForm.type);
      if (vesselForm.capacity) formData.append('capacity', vesselForm.capacity);

      const response = await axios.put(`https://ecl.net.in/backende/api/vessels/${editingId}`, formData);

      if (response.data.success) {
        setVesselForm({ 
          ...vesselForm, 
          name: "", 
          type: "", 
          capacity: "" 
        });
        setEditingId(null);
        setIsEditing(false);
        setShowVesselForm(false);
        fetchAllData();
        showSuccess("Vessel updated successfully!");
      } else {
        showError(response.data.message || "Failed to update vessel");
      }
    } catch (err) {
      showError("Failed to update vessel");
      console.error(err);
    }
  };

  const handleDeleteVessel = async (vesselId) => {
    if (window.confirm("Are you sure? This will delete all schedules for this vessel.")) {
      try {
        const response = await axios.delete(`https://ecl.net.in/backende/api/vessels/${vesselId}`);

        if (response.data.success) {
          fetchAllData();
          showSuccess("Vessel deleted successfully!");
        } else {
          showError(response.data.message || "Failed to delete vessel");
        }
      } catch (err) {
        showError("Failed to delete vessel");
        console.error(err);
      }
    }
  };

  // Port CRUD operations
  const handleAddPort = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('name', portForm.name);
      if (portForm.country) formData.append('country', portForm.country);
      if (portForm.code) formData.append('code', portForm.code);

      const response = await axios.post('https://ecl.net.in/backende/api/ports', formData);

      if (response.data.success) {
        setPortForm({ name: "", country: "", code: "" });
        setShowPortForm(false);
        fetchAllData();
        showSuccess("Port added successfully!");
      } else {
        showError(response.data.message || "Failed to add port");
      }
    } catch (err) {
      showError("Failed to add port");
      console.error(err);
    }
  };

  const handleEditPort = (port) => {
    setPortForm({
      name: port.name,
      country: port.country || "",
      code: port.code || ""
    });
    setEditingId(port.id);
    setIsEditing(true);
    setShowPortForm(true);
  };

  const handleUpdatePort = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('name', portForm.name);
      if (portForm.country) formData.append('country', portForm.country);
      if (portForm.code) formData.append('code', portForm.code);

      const response = await axios.put(`https://ecl.net.in/backende/api/ports/${editingId}`, formData);

      if (response.data.success) {
        setPortForm({ name: "", country: "", code: "" });
        setEditingId(null);
        setIsEditing(false);
        setShowPortForm(false);
        fetchAllData();
        showSuccess("Port updated successfully!");
      } else {
        showError(response.data.message || "Failed to update port");
      }
    } catch (err) {
      showError("Failed to update port");
      console.error(err);
    }
  };

  const handleDeletePort = async (portId) => {
    if (window.confirm("Are you sure? This will delete all schedules for this port.")) {
      try {
        const response = await axios.delete(`https://ecl.net.in/backende/api/ports/${portId}`);

        if (response.data.success) {
          fetchAllData();
          showSuccess("Port deleted successfully!");
        } else {
          showError(response.data.message || "Failed to delete port");
        }
      } catch (err) {
        showError("Failed to delete port");
        console.error(err);
      }
    }
  };

  // Schedule CRUD operations
  const handleAddSchedule = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('vessel_id', scheduleForm.vessel_id);
      formData.append('port_id', scheduleForm.port_id);
      formData.append('dates', scheduleForm.dates);
      if (scheduleForm.eta) formData.append('eta', scheduleForm.eta);
      if (scheduleForm.etd) formData.append('etd', scheduleForm.etd);

      const response = await axios.post('https://ecl.net.in/backende/api/schedules', formData);

      if (response.data.success) {
        setScheduleForm({ 
          ...scheduleForm, 
          dates: "", 
          eta: "", 
          etd: "" 
        });
        setShowScheduleForm(false);
        fetchAllData();
        showSuccess("Schedule added successfully!");
      } else {
        showError(response.data.message || "Failed to add schedule");
      }
    } catch (err) {
      showError("Failed to add schedule");
      console.error(err);
    }
  };

  const handleEditSchedule = (schedule) => {
    // Find the vessel to get its route_id
    const vessel = vessels.find(v => v.id === schedule.vessel_id);
    const routeId = vessel ? vessel.route_id : "";
    
    setScheduleForm({
      vessel_id: schedule.vessel_id,
      port_id: schedule.port_id,
      dates: schedule.dates,
      eta: schedule.eta || "",
      etd: schedule.etd || "",
      route_key: routeId // Add the route key to properly filter vessels
    });
    setEditingId(schedule.id);
    setIsEditing(true);
    setShowScheduleForm(true);
  };

  const handleUpdateSchedule = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('vessel_id', scheduleForm.vessel_id);
      formData.append('port_id', scheduleForm.port_id);
      formData.append('dates', scheduleForm.dates);
      if (scheduleForm.eta) formData.append('eta', scheduleForm.eta);
      if (scheduleForm.etd) formData.append('etd', scheduleForm.etd);

      const response = await axios.put(`https://ecl.net.in/backende/api/schedules/${editingId}`, formData);

      if (response.data.success) {
        setScheduleForm({ 
          ...scheduleForm, 
          dates: "", 
          eta: "", 
          etd: "" 
        });
        setEditingId(null);
        setIsEditing(false);
        setShowScheduleForm(false);
        fetchAllData();
        showSuccess("Schedule updated successfully!");
      } else {
        showError(response.data.message || "Failed to update schedule");
      }
    } catch (err) {
      showError("Failed to update schedule");
      console.error(err);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (window.confirm("Are you sure you want to delete this schedule?")) {
      try {
        const response = await axios.delete(`https://ecl.net.in/backende/api/schedules/${scheduleId}`);

        if (response.data.success) {
          fetchAllData();
          showSuccess("Schedule deleted successfully!");
        } else {
          showError(response.data.message || "Failed to delete schedule");
        }
      } catch (err) {
        showError("Failed to delete schedule");
        console.error(err);
      }
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingId(null);

    if (activeTab === "routes") {
      setRouteForm({ direction: "" });
      setShowRouteForm(false);
    } else if (activeTab === "vessels") {
      setVesselForm({ 
        name: "", 
        route_id: routes.length > 0 ? routes[0].id : "", 
        type: "", 
        capacity: "" 
      });
      setShowVesselForm(false);
    } else if (activeTab === "ports") {
      setPortForm({ name: "", country: "", code: "" });
      setShowPortForm(false);
    } else if (activeTab === "schedules") {
      const defaultRouteId = routes.length > 0 ? routes[0].id : "";
      const routeVessels = vessels.filter(vessel => vessel.route_id === defaultRouteId);
      
      setScheduleForm({
        vessel_id: routeVessels.length > 0 ? routeVessels[0].id : "",
        port_id: ports.length > 0 ? ports[0].id : "",
        dates: "",
        eta: "",
        etd: "",
        route_key: defaultRouteId
      });
      setShowScheduleForm(false);
    }
  };

  // Pagination functions
  const paginate = (pageNumber, setPage) => {
    setPage(pageNumber);
  };

  // Filtering functions
  const filteredRoutes = routes.filter(route =>
    route.direction.toLowerCase().includes(routeSearch.toLowerCase())
  );

  const filteredVessels = vessels.filter(vessel =>
    vessel.name.toLowerCase().includes(vesselSearch.toLowerCase())
  );

  const filteredPorts = ports.filter(port =>
    port.name.toLowerCase().includes(portSearch.toLowerCase())
  );

  const filteredSchedules = schedules.filter(schedule =>
    schedule.vessel_name.toLowerCase().includes(scheduleSearch.toLowerCase()) ||
    schedule.port_name.toLowerCase().includes(scheduleSearch.toLowerCase())
  );

  // Pagination data
  const indexOfLastRoute = currentRoutePage * itemsPerPage;
  const indexOfFirstRoute = indexOfLastRoute - itemsPerPage;
  const currentRoutes = filteredRoutes.slice(indexOfFirstRoute, indexOfLastRoute);

  const indexOfLastVessel = currentVesselPage * itemsPerPage;
  const indexOfFirstVessel = indexOfLastVessel - itemsPerPage;
  const currentVessels = filteredVessels.slice(indexOfFirstVessel, indexOfLastVessel);

  const indexOfLastPort = currentPortPage * itemsPerPage;
  const indexOfFirstPort = indexOfLastPort - itemsPerPage;
  const currentPorts = filteredPorts.slice(indexOfFirstPort, indexOfLastPort);

  const indexOfLastSchedule = currentSchedulePage * itemsPerPage;
  const indexOfFirstSchedule = indexOfLastSchedule - itemsPerPage;
  const currentSchedules = filteredSchedules.slice(indexOfFirstSchedule, indexOfLastSchedule);

  // Render pagination controls
  const renderPagination = (itemsCount, currentPage, setCurrentPage) => {
    const pageNumbers = [];
    for (let i = 1; i <= Math.ceil(itemsCount / itemsPerPage); i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="admin-pagination">
        {currentPage > 1 && (
          <button
            onClick={() => paginate(currentPage - 1, setCurrentPage)}
            className="admin-pagination-button"
          >
            &lt; Previous
          </button>
        )}

        {pageNumbers.map(number => (
          <button
            key={number}
            onClick={() => paginate(number, setCurrentPage)}
            className={`admin-pagination-button ${currentPage === number ? 'active' : ''}`}
          >
            {number}
          </button>
        ))}

        {currentPage < pageNumbers.length && (
          <button
            onClick={() => paginate(currentPage + 1, setCurrentPage)}
            className="admin-pagination-button"
          >
            Next &gt;
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="admin-loading-overlay">
      <div className="admin-loading-spinner"></div>
      <div>Loading schedule data...</div>
    </div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-page-header">
        <h1>Sailing Schedule Management</h1>
        <p className="admin-subtitle">Manage your sailing routes, vessels, ports, and schedules</p>
      </div>

      {error && <div className="admin-alert error">{error}</div>}
      {success && <div className="admin-alert success">{success}</div>}

      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'routes' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('routes');
            setShowRouteForm(false);
            setShowVesselForm(false);
            setShowPortForm(false);
            setShowScheduleForm(false);
          }}
        >
          Routes
        </button>
        <button
          className={`admin-tab ${activeTab === 'vessels' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('vessels');
            setShowRouteForm(false);
            setShowVesselForm(false);
            setShowPortForm(false);
            setShowScheduleForm(false);
          }}
        >
          Vessels
        </button>
        <button
          className={`admin-tab ${activeTab === 'ports' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('ports');
            setShowRouteForm(false);
            setShowVesselForm(false);
            setShowPortForm(false);
            setShowScheduleForm(false);
          }}
        >
          Ports
        </button>
        <button
          className={`admin-tab ${activeTab === 'schedules' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('schedules');
            setShowRouteForm(false);
            setShowVesselForm(false);
            setShowPortForm(false);
            setShowScheduleForm(false);
          }}
        >
          Schedules
        </button>
      </div>

      <div className="admin-content">
        {/* Routes Tab */}
        {activeTab === 'routes' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <div className="admin-section-title">
                {/* <h2>Routes</h2> */}
              </div>
              <button
                className="admin-add-button"
                onClick={() => {
                  setIsEditing(false);
                  setEditingId(null);
                  setRouteForm({ direction: "" });
                  setShowRouteForm(!showRouteForm);
                }}
              >
                + Add New Route
              </button>
            </div>

            {showRouteForm && (
              <div className="admin-form-container">
                <h3>{isEditing ? "Edit Route" : "Add Route"}</h3>
                <form onSubmit={isEditing ? handleUpdateRoute : handleAddRoute}>
                  <div className="form-group">
                    <label>Direction:</label>
                    <input
                      type="text"
                      value={routeForm.direction}
                      onChange={(e) => setRouteForm({ direction: e.target.value })}
                      placeholder="e.g., EX JAPAN / S.E.A TO B.O.B / INDIA ( IMPORT )"
                      required
                    />
                  </div>
                  <div className="form-buttons">
                    <button type="submit" className="admin-save-button">
                      {isEditing ? "Update Route" : "Add Route"}
                    </button>
                    <button
                      type="button"
                      className="admin-cancel-button"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="admin-list-container">
              <div className="admin-list-header">
                <h3>Routes List</h3>
                <div className="admin-search-container">
                  <input
                    type="text"
                    placeholder="Search routes..."
                    value={routeSearch}
                    onChange={(e) => setRouteSearch(e.target.value)}
                    className="admin-search-input"
                  />
                </div>
              </div>

              {filteredRoutes.length === 0 ? (
                <div className="admin-empty-message">No routes found. Add a route to get started.</div>
              ) : (
                <>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Direction</th>
                        <th className="action-column">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentRoutes.map(route => (
                        <tr key={route.id}>
                          <td>{route.direction}</td>
                          <td className="action-buttons">
                            <button
                              className="admin-edit-button"
                              onClick={() => handleEditRoute(route)}
                            >
                              Edit
                            </button>
                            <button
                              className="admin-delete-button"
                              onClick={() => handleDeleteRoute(route.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {renderPagination(filteredRoutes.length, currentRoutePage, setCurrentRoutePage)}
                </>
              )}
            </div>
          </div>
        )}

        {/* Vessels Tab */}
        {activeTab === 'vessels' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <div className="admin-section-title">
                {/* <h2>Vessels</h2> */}
              </div>
              <button
                className="admin-add-button"
                onClick={() => {
                  setIsEditing(false);
                  setEditingId(null);
                  setVesselForm({
                    name: "",
                    route_id: routes.length > 0 ? routes[0].id : "",
                    type: "",
                    capacity: ""
                  });
                  setShowVesselForm(!showVesselForm);
                }}
              >
                + Add New Vessel
              </button>
            </div>

            {showVesselForm && (
              <div className="admin-form-container">
                <h3>{isEditing ? "Edit Vessel" : "Add Vessel"}</h3>
                <form onSubmit={isEditing ? handleUpdateVessel : handleAddVessel}>

                  <div className="form-group">
                    <label>Route:</label>
                    <select
                      value={vesselForm.route_id}
                      onChange={(e) => setVesselForm({ ...vesselForm, route_id: e.target.value })}
                      required
                    >
                      {routes.map(route => (
                        <option key={route.id} value={route.id}>
                          {route.direction}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Name:</label>
                    <input
                      type="text"
                      value={vesselForm.name}
                      onChange={(e) => setVesselForm({ ...vesselForm, name: e.target.value })}
                      placeholder="e.g., MV TOYO PEONY"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Type (optional):</label>
                    <input
                      type="text"
                      value={vesselForm.type}
                      onChange={(e) => setVesselForm({ ...vesselForm, type: e.target.value })}
                      placeholder="e.g., Bulk Carrier"
                    />
                  </div>
                  <div className="form-group">
                    <label>Capacity (optional):</label>
                    <input
                      type="text"
                      value={vesselForm.capacity}
                      onChange={(e) => setVesselForm({ ...vesselForm, capacity: e.target.value })}
                      placeholder="e.g., 65,000 DWT"
                    />
                  </div>
                  <div className="form-buttons">
                    <button type="submit" className="admin-save-button">
                      {isEditing ? "Update Vessel" : "Add Vessel"}
                    </button>
                    <button
                      type="button"
                      className="admin-cancel-button"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="admin-list-container">
              <div className="admin-list-header">
                <h3>Vessels List</h3>
                <div className="admin-search-container">
                  <input
                    type="text"
                    placeholder="Search vessels..."
                    value={vesselSearch}
                    onChange={(e) => setVesselSearch(e.target.value)}
                    className="admin-search-input"
                  />
                </div>
              </div>

              {filteredVessels.length === 0 ? (
                <div className="admin-empty-message">No vessels found. Add a vessel to get started.</div>
              ) : (
                <>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Route</th>
                        <th>Type</th>
                        <th>Capacity</th>
                        <th className="action-column">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentVessels.map(vessel => (
                        <tr key={vessel.id}>
                          <td>{vessel.name}</td>
                          <td>{vessel.route_direction}</td>
                          <td>{vessel.type || '-'}</td>
                          <td>{vessel.capacity || '-'}</td>
                          <td className="action-buttons">
                          <button
                              className="admin-edit-button"
                              onClick={() => handleEditVessel(vessel)}
                            >
                              Edit
                            </button>
                            <button
                              className="admin-delete-button"
                              onClick={() => handleDeleteVessel(vessel.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {renderPagination(filteredVessels.length, currentVesselPage, setCurrentVesselPage)}
                </>
              )}
            </div>
          </div>
        )}

        {/* Ports Tab */}
        {activeTab === 'ports' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <div className="admin-section-title">
                {/* <h2>Ports</h2> */}
              </div>
              <button
                className="admin-add-button"
                onClick={() => {
                  setIsEditing(false);
                  setEditingId(null);
                  setPortForm({ name: "", country: "", code: "" });
                  setShowPortForm(!showPortForm);
                }}
              >
                + Add New Port
              </button>
            </div>

            {showPortForm && (
              <div className="admin-form-container">
                <h3>{isEditing ? "Edit Port" : "Add Port"}</h3>
                <form onSubmit={isEditing ? handleUpdatePort : handleAddPort}>
                  {/* Removed the route selection from Port form since ports aren't tied to specific routes */}
                  <div className="form-group">
                    <label>Name:</label>
                    <input
                      type="text"
                      value={portForm.name}
                      onChange={(e) => setPortForm({ ...portForm, name: e.target.value })}
                      placeholder="e.g., KIMITSU"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Country (optional):</label>
                    <input
                      type="text"
                      value={portForm.country}
                      onChange={(e) => setPortForm({ ...portForm, country: e.target.value })}
                      placeholder="e.g., JAPAN"
                    />
                  </div>
                  <div className="form-group">
                    <label>Code (optional):</label>
                    <input
                      type="text"
                      value={portForm.code}
                      onChange={(e) => setPortForm({ ...portForm, code: e.target.value })}
                      placeholder="e.g., KMT"
                    />
                  </div>
                  <div className="form-buttons">
                    <button type="submit" className="admin-save-button">
                      {isEditing ? "Update Port" : "Add Port"}
                    </button>
                    <button
                      type="button"
                      className="admin-cancel-button"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="admin-list-container">
              <div className="admin-list-header">
                <h3>Ports List</h3>
                <div className="admin-search-container">
                  <input
                    type="text"
                    placeholder="Search ports..."
                    value={portSearch}
                    onChange={(e) => setPortSearch(e.target.value)}
                    className="admin-search-input"
                  />
                </div>
              </div>

              {filteredPorts.length === 0 ? (
                <div className="admin-empty-message">No ports found. Add a port to get started.</div>
              ) : (
                <>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Country</th>
                        <th>Code</th>
                        <th className="action-column">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentPorts.map(port => (
                        <tr key={port.id}>
                          <td>{port.name}</td>
                          <td>{port.country || '-'}</td>
                          <td>{port.code || '-'}</td>
                          <td className="action-buttons">
                            <button
                              className="admin-edit-button"
                              onClick={() => handleEditPort(port)}
                            >
                              Edit
                            </button>
                            <button
                              className="admin-delete-button"
                              onClick={() => handleDeletePort(port.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {renderPagination(filteredPorts.length, currentPortPage, setCurrentPortPage)}
                </>
              )}
            </div>
          </div>
        )}

        {/* Schedules Tab */}
        {activeTab === 'schedules' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <div className="admin-section-title">
                {/* <h2>Schedules</h2> */}
              </div>
              <button
                className="admin-add-button"
                onClick={() => {
                  setIsEditing(false);
                  setEditingId(null);
                  // Initialize with proper route and vessel filtering
                  const defaultRouteId = routes.length > 0 ? routes[0].id : "";
                  const routeVessels = vessels.filter(vessel => vessel.route_id === defaultRouteId);
                  
                  setScheduleForm({
                    vessel_id: routeVessels.length > 0 ? routeVessels[0].id : "",
                    port_id: ports.length > 0 ? ports[0].id : "",
                    dates: "",
                    eta: "",
                    etd: "",
                    route_key: defaultRouteId
                  });
                  setShowScheduleForm(!showScheduleForm);
                }}
              >
                + Add New Schedule
              </button>
            </div>

            {showScheduleForm && (
              <div className="admin-form-container">
                <h3>{isEditing ? "Edit Schedule" : "Add Schedule"}</h3>
                <form onSubmit={isEditing ? handleUpdateSchedule : handleAddSchedule}>
                  <div className="form-group">
                    <label>Route:</label>
                    <select
                      value={scheduleForm.route_key || ""}
                      onChange={(e) => {
                        const newRouteId = e.target.value;
                        // Filter vessels to only show those for the selected route
                        const routeVessels = vessels.filter(vessel => vessel.route_id === newRouteId);
                        
                        setScheduleForm({
                          ...scheduleForm,
                          route_key: newRouteId,
                          // Set the first vessel from the filtered list as the default
                          vessel_id: routeVessels.length > 0 ? routeVessels[0].id : ""
                        });
                      }}
                      required
                    >
                      {routes.map(route => (
                        <option key={route.id} value={route.id}>
                          {route.direction}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Vessel:</label>
                    <select
                      value={scheduleForm.vessel_id}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, vessel_id: e.target.value })}
                      required
                    >
                      {/* Only show vessels for the selected route */}
                      {vessels
                        .filter(vessel => vessel.route_id === scheduleForm.route_key)
                        .map(vessel => (
                          <option key={vessel.id} value={vessel.id}>
                            {vessel.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Port:</label>
                    <select
                      value={scheduleForm.port_id}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, port_id: e.target.value })}
                      required
                    >
                      {ports.map(port => (
                        <option key={port.id} value={port.id}>
                          {port.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Dates:</label>
                    <input
                      type="text"
                      value={scheduleForm.dates}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, dates: e.target.value })}
                      placeholder="e.g., 29-30 Jan"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>ETA (optional):</label>
                    <input
                      type="text"
                      value={scheduleForm.eta}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, eta: e.target.value })}
                      placeholder="e.g., 29 Jan 08:00"
                    />
                  </div>
                  <div className="form-group">
                    <label>ETD (optional):</label>
                    <input
                      type="text"
                      value={scheduleForm.etd}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, etd: e.target.value })}
                      placeholder="e.g., 30 Jan 18:00"
                    />
                  </div>
                  <div className="form-buttons">
                    <button type="submit" className="admin-save-button">
                      {isEditing ? "Update Schedule" : "Add Schedule"}
                    </button>
                    <button
                      type="button"
                      className="admin-cancel-button"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="admin-list-container">
              <div className="admin-list-header">
                <h3>Schedule List</h3>
                <div className="admin-search-container">
                  <input
                    type="text"
                    placeholder="Search schedules..."
                    value={scheduleSearch}
                    onChange={(e) => setScheduleSearch(e.target.value)}
                    className="admin-search-input"
                  />
                </div>
              </div>

              {filteredSchedules.length === 0 ? (
                <div className="admin-empty-message">No schedules found. Add a schedule to get started.</div>
              ) : (
                <>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Vessel</th>
                        <th>Port</th>
                        <th>Dates</th>
                        <th>ETA</th>
                        <th>ETD</th>
                        <th className="action-column">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentSchedules.map(schedule => (
                        <tr key={schedule.id}>
                          <td>{schedule.vessel_name}</td>
                          <td>{schedule.port_name}</td>
                          <td>{schedule.dates}</td>
                          <td>{schedule.eta || '-'}</td>
                          <td>{schedule.etd || '-'}</td>
                          <td className="action-buttons">
                            <button
                              className="admin-edit-button"
                              onClick={() => handleEditSchedule(schedule)}
                            >
                              Edit
                            </button>
                            <button
                              className="admin-delete-button"
                              onClick={() => handleDeleteSchedule(schedule.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {renderPagination(filteredSchedules.length, currentSchedulePage, setCurrentSchedulePage)}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSchedule;